/**
 * schedulerRole.js
 * ──────────────────────────────────────────────────────────────────────
 * Helper to resolve which scheduler role layout the current user should
 * see (Edit 3 & Edit 5 of scheduler_edits_v2.md).
 *
 * Real mode:   resolved from `user.app_role` (live access mapping).
 * Demo mode:   caller supplies an explicit `demoRole` from the demo-only
 *              role switcher (see DemoRoleSwitcher.js).
 *
 * Four roles drive the UI: 'front_desk', 'ma', 'provider', 'manager'.
 */

export const SCHEDULER_ROLES = [
  { id: 'front_desk', label: 'Front Desk' },
  { id: 'ma', label: 'Medical Assistant' },
  { id: 'provider', label: 'Provider' },
  { id: 'manager', label: 'Manager' },
];

export const DEFAULT_SCHEDULER_ROLE = 'front_desk';

/**
 * Map the real-mode access role to a scheduler role.
 * Mapping strategy (aligned with existing app_role taxonomy):
 *   super_admin | admin            → manager
 *   front_desk                     → front_desk
 *   provider | clinical_admin      → provider
 *   medical_assistant | nurse_rn   → ma
 *   read_only | scribe | unknown   → provider (read-only surface)
 */
export const mapAppRoleToSchedulerRole = (appRole) => {
  switch (appRole) {
    case 'super_admin':
    case 'admin':
      return 'manager';
    case 'front_desk':
      return 'front_desk';
    case 'provider':
    case 'clinical_admin':
      return 'provider';
    case 'medical_assistant':
    case 'nurse_rn':
      return 'ma';
    case 'read_only':
    case 'scribe':
    default:
      return 'provider';
  }
};

/**
 * Resolve the current scheduler role.
 * @param {object} opts
 * @param {boolean} opts.isDemo
 * @param {string|null} opts.demoRole  — demo-only selector value
 * @param {object|null} opts.user      — real-mode user object (must have app_role/role)
 * @returns {'front_desk'|'ma'|'provider'|'manager'}
 */
export const resolveSchedulerRole = ({ isDemo, demoRole, user }) => {
  if (isDemo) return demoRole || DEFAULT_SCHEDULER_ROLE;
  const appRole = user?.app_role || user?.role || null;
  return mapAppRoleToSchedulerRole(appRole);
};

export default resolveSchedulerRole;
